import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout, { Section } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import DetailNotFound from "@/components/common/DetailNotFound";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function CaseStudyDetail() {
    const { industry, slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        // Reset on every industry/slug change so a previous case study (or
        // a previous "not found") doesn't stay on screen while the new
        // request is in flight, and ignore a stale response that lands
        // after a newer one already resolved or after unmount.
        let cancelled = false;
        setData(null);
        setErr(false);
        api.get(`/case-studies/${industry}/${slug}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setErr(true); });
        return () => { cancelled = true; };
    }, [industry, slug]);

    if (err) {
        return (
            <DetailNotFound title="Not found — Ventara" heading="This case study is not here."
                            backTo="/industries" backLabel="Back to Industries" />
        );
    }
    if (!data) {
        return (
            <Layout title="Loading — Ventara" description="Loading this case study.">
                <Section className="pt-40"><p className="prose-body">Loading…</p></Section>
            </Layout>
        );
    }

    return (
        <Layout title={`${data.title} — Ventara`} description={data.note}>
            <Section className="pt-32">
                <Link to={`/industries/${industry}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> {industry.replace(/-/g," ")}
                </Link>
                <p className="label mt-10">{data.client}</p>
                <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tight leading-[0.98] max-w-4xl">{data.title}</h1>
                <p className="prose-body text-lg mt-10 max-w-2xl">{data.note}</p>
            </Section>

            <Section className="py-16">
                <div className="grid lg:grid-cols-3 gap-8 border-y border-border py-10">
                    <div>
                        <p className="label">Outcome</p>
                        <p className="font-serif text-3xl mt-3 leading-tight">{data.outcome}</p>
                    </div>
                    <div>
                        <p className="label">Industry</p>
                        <p className="font-serif text-2xl mt-3 capitalize">{industry.replace(/-/g," ")}</p>
                    </div>
                    <div>
                        <p className="label">Client</p>
                        <p className="font-serif text-2xl mt-3">{data.client}</p>
                    </div>
                </div>
            </Section>

            <Section className="pb-32 space-y-16">
                {data.sections?.map((s, i) => (
                    <Reveal key={i}>
                        <article className="grid lg:grid-cols-12 gap-12" data-testid={`case-section-${i}`}>
                            <div className="lg:col-span-4"><p className="label">Chapter 0{i + 1}</p><h2 className="font-serif text-3xl sm:text-4xl mt-3 tracking-tight">{s.h}</h2></div>
                            <div className="lg:col-span-8 prose-body text-lg">{s.p}</div>
                        </article>
                    </Reveal>
                ))}
            </Section>
        </Layout>
    );
}
